import { H2 } from 'UIComponents/elements/headings';
import { OLAF } from 'HubStyleTokens/colors';
import { useContext, useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIModal from 'UIComponents/dialog/UIModal';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import UITextInput from 'UIComponents/input/UITextInput';
import { RegexSafetyLevels, isSafeRegexUserInput, isRegex } from '../../utils/regexUtils';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { Text } from 'foundations-components/components/Text';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const sanitizeOptions = options => {
  // Remove duplicates and empty strings, also trim whitespace
  return [...new Set(options.map(s => s.trim()).filter(s => s.length > 0))];
};
const textToOptions = (text, delimiter) => {
  const useDelimiter = delimiter && delimiter.length > 0;
  if (!useDelimiter) {
    return sanitizeOptions(text.split('\n'));
  }
  const delimiterIsRegex = isRegex(delimiter);
  if (delimiterIsRegex) {
    try {
      if (!isSafeRegexUserInput(delimiter, RegexSafetyLevels.DEFAULT)) {
        throw new Error('ReDoS validation failed');
      }
      const regex = new RegExp(delimiter.substring(1, delimiter.length - 1));
      return sanitizeOptions(text.split(regex));
    } catch (e) {
      return sanitizeOptions(text.split('\n'));
    }
  }
  return sanitizeOptions(text.split(delimiter));
};
const MultiValueEditor = ({
  onCancel: onCancelHandler,
  onConfirm: onConfirmHandler,
  value
}) => {
  const [text, setText] = useState(value.join('\n') || '');
  const [options, setOptions] = useState(textToOptions(value.join('\n') || ''));
  const [delimiter, setDelimiter] = useState('');
  const {
    getTrackerInstance,
    props: {
      baseFilterFamily,
      useOnePanel
    }
  } = useContext(SegmentationFiltersInstanceContext);
  useEffect(() => {
    var _getTrackerInstance;
    (_getTrackerInstance = getTrackerInstance()) === null || _getTrackerInstance === void 0 || _getTrackerInstance.track('openedMultiValueEditor', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
  }, [getTrackerInstance, baseFilterFamily, useOnePanel]);
  useEffect(() => {
    if (delimiter) {
      var _getTrackerInstance2;
      (_getTrackerInstance2 = getTrackerInstance()) === null || _getTrackerInstance2 === void 0 || _getTrackerInstance2.track('usedDelimiterInMultiValueEditor', {
        primaryObjectTypeId: baseFilterFamily,
        value: delimiter,
        version: useOnePanel ? '2' : '1'
      });
    }
  }, [getTrackerInstance, baseFilterFamily, delimiter, useOnePanel]);
  const delimiterIsInvalid = delimiter.length > 0 && isRegex(delimiter) && !isSafeRegexUserInput(delimiter, RegexSafetyLevels.DEFAULT);
  const onCancel = () => {
    var _getTrackerInstance3;
    onCancelHandler();
    (_getTrackerInstance3 = getTrackerInstance()) === null || _getTrackerInstance3 === void 0 || _getTrackerInstance3.track('canceledMultiValueEditor', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
  };
  const onSubmit = evt => {
    var _getTrackerInstance4;
    evt.preventDefault();
    onConfirmHandler(options);
    (_getTrackerInstance4 = getTrackerInstance()) === null || _getTrackerInstance4 === void 0 || _getTrackerInstance4.track('confirmedMultiValueEditor', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
  };
  return /*#__PURE__*/_jsx(UIModal, {
    children: /*#__PURE__*/_jsxs(UIForm, {
      onSubmit: onSubmit,
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.header"
          })
        }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: () => onCancel()
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [/*#__PURE__*/_jsx(UIFormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.textAreaLabel"
          }),
          children: /*#__PURE__*/_jsx(TextArea, {
            blockSize: "100px",
            value: text,
            onChange: e => {
              setText(e.target.value);
              setOptions(textToOptions(e.target.value, delimiter));
            }
          })
        }), /*#__PURE__*/_jsx(UIFormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.delimiterLabel"
          }),
          children: /*#__PURE__*/_jsx(UITextInput, {
            error: delimiterIsInvalid,
            onChange: e => {
              setDelimiter(e.target.value);
              setOptions(textToOptions(text, e.target.value));
            }
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "small",
          display: "inline-block",
          use: "help",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.delimiterHelp"
          })
        }), /*#__PURE__*/_jsx(UIFormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.preview",
            options: {
              count: options.length
            }
          }),
          children: /*#__PURE__*/_jsxs(UIScrollContainer, {
            maxHeight: 100,
            gradientColor: OLAF,
            size: 28,
            children: [options.map(option => /*#__PURE__*/_jsx(Tag
            // Default margin-left is 12px but it leads to rows looking tabbed
            , {
              marginInlineStart: "0 !important",
              marginInlineEnd: "300",
              use: "default",
              children: option
            }, option)), options.length === 0 && /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.previewPlaceholder"
            })]
          })
        })]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UIButton, {
          use: "primary",
          type: "submit",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.confirmButton"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: onCancel,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.customerDataFilters.MultiValueEditor.cancelButton"
          })
        })]
      })]
    })
  });
};
export default MultiValueEditor;